import type { UserProfile } from "@yutai/shared"
import { useEffect } from "react"
import useSWR, { mutate as globalMutate } from "swr"
import { trackEvent } from "./analytics.js"

// In production /auth/* is same-origin via Cloudflare Pages Functions that
// proxy to the Worker (see apps/web/functions/). In `pnpm dev` there's no
// Pages proxy, so requests go directly to the Worker's own origin
// (cross-origin, relies on the credentialed CORS setup in apps/worker's
// index.ts).
const AUTH_BASE = import.meta.env.DEV ? (import.meta.env.VITE_WORKER_URL ?? "") : ""
const ME_KEY = `${AUTH_BASE}/auth/me`

async function fetchMe(url: string): Promise<UserProfile | null> {
  const res = await fetch(url, { credentials: "include" })
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`)
  return res.json()
}

export function useAuth() {
  const { data, isLoading } = useSWR<UserProfile | null>(ME_KEY, fetchMe, {
    revalidateOnFocus: false,
  })
  return {
    user: data ?? null,
    isLoggedIn: Boolean(data),
    isLoading,
  }
}

export function refreshAuth(): Promise<UserProfile | null | undefined> {
  return globalMutate(ME_KEY)
}

export function loginWithGoogle(returnTo?: string): void {
  trackEvent("login_started")
  const target = returnTo ?? window.location.pathname + window.location.search
  window.location.href = `${AUTH_BASE}/auth/google/start?returnTo=${encodeURIComponent(target)}`
}

export async function logout(): Promise<void> {
  await fetch(`${AUTH_BASE}/auth/logout`, { method: "POST", credentials: "include" })
  await refreshAuth()
}

export async function loginBackdoor(id: string, password: string): Promise<boolean> {
  const res = await fetch(`${AUTH_BASE}/auth/backdoor/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, password }),
  })
  return res.ok
}

// Mounted once near the app root: after the Google OAuth callback redirects
// back with ?loginSuccess=1, fires the login_completed funnel event, makes
// sure useAuth() picks up the now-logged-in state, and strips the marker
// param so it doesn't linger in the URL / re-fire on refresh.
export function useLoginSuccessTracking(): void {
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get("loginSuccess") !== "1") return

    trackEvent("login_completed")
    refreshAuth()

    url.searchParams.delete("loginSuccess")
    window.history.replaceState(null, "", url.pathname + url.search + url.hash)
  }, [])
}
